import React, { useEffect, useState } from 'react'
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'

// Pending student submissions across steps, for the admin/office-manager
// dashboard. RLS scopes reads: admin/office_manager see all students;
// instructors/mentors see only their own — so this is safe on any staff view.
// Mirrors what mentors get in Action Items, in one place for oversight.

const STEP_LABEL: Record<number, string> = { 1: 'Topic', 2: 'Research Question', 3: 'Background Research', 4: 'Hypothesis', 5: 'Experimental Design', 6: 'Experiment' }

interface SubItem {
  project_id:   string
  student_name: string
  step:         number
  submitted_at: string
}

export default function PendingSubmissionsPanel() {
  const navigate = useNavigate()
  const [items,   setItems]   = useState<SubItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      setError(null)
      try {
        // Supabase resolves (never throws) on RLS/application errors, so every
        // destructure below must check its own `error` — otherwise a transient
        // failure on any ONE query silently zeroes out that section instead of
        // surfacing a retry (same class of bug fixed in ActionItemsPanel).
        const bail = (err: { message: string } | null, label: string) => {
          if (err) throw new Error(`${label}: ${err.message}`)
        }

        const [
          { data: topics, error: topicsErr }, { data: rqs, error: rqsErr },
          { data: bgs, error: bgsErr }, { data: hds, error: hdsErr },
          { data: eds, error: edsErr }, { data: ers, error: ersErr },
        ] = await Promise.all([
          supabase.from('research_topic').select('project_id, updated_at').eq('status', 'submitted'),
          supabase.from('research_question').select('project_id, updated_at').eq('status', 'submitted'),
          supabase.from('background_research').select('project_id, updated_at').eq('status', 'submitted'),
          supabase.from('hypothesis_design').select('project_id, updated_at').eq('status', 'submitted'),
          supabase.from('experiment_design').select('project_id, updated_at').eq('status', 'submitted'),
          supabase.from('experiment_record').select('project_id, updated_at').eq('status', 'submitted'),
        ])
        bail(topicsErr, 'loading topic submissions'); bail(rqsErr, 'loading Step 2 drafts')
        bail(bgsErr, 'loading Step 3 submissions'); bail(hdsErr, 'loading Step 4 submissions')
        bail(edsErr, 'loading Step 5 submissions'); bail(ersErr, 'loading Step 6 submissions')

        const raw: { project_id: string; step: number; submitted_at: string }[] = [
          ...(topics ?? []).map(t => ({ project_id: t.project_id, step: 1, submitted_at: t.updated_at })),
          ...(rqs ?? []).map(r => ({ project_id: r.project_id, step: 2, submitted_at: r.updated_at })),
          ...(bgs ?? []).map(b => ({ project_id: b.project_id, step: 3, submitted_at: b.updated_at })),
          ...(hds ?? []).map(h => ({ project_id: h.project_id, step: 4, submitted_at: h.updated_at })),
          ...(eds ?? []).map(e => ({ project_id: e.project_id, step: 5, submitted_at: e.updated_at })),
          ...(ers ?? []).map(e => ({ project_id: e.project_id, step: 6, submitted_at: e.updated_at })),
        ]
        if (raw.length === 0) { setItems([]); setLoading(false); return }

        // project → student → name
        const projectIds = Array.from(new Set(raw.map(r => r.project_id)))
        const { data: projects } = await supabase
          .from('project').select('project_id, student_id').in('project_id', projectIds)
        const studentByProject: Record<string, string> = Object.fromEntries((projects ?? []).map(p => [p.project_id, p.student_id]))
        const studentIds = Array.from(new Set(Object.values(studentByProject)))
        const { data: sps } = studentIds.length
          ? await supabase.from('student_profile').select('student_id, user_id').in('student_id', studentIds)
          : { data: [] as { student_id: string; user_id: string }[] }
        const userByStudent: Record<string, string> = Object.fromEntries((sps ?? []).map(s => [s.student_id, s.user_id]))
        const userIds = Array.from(new Set(Object.values(userByStudent)))
        const { data: users } = userIds.length
          ? await supabase.from('users').select('user_id, first_name, last_name, email').in('user_id', userIds)
          : { data: [] as { user_id: string; first_name: string; last_name: string; email: string }[] }
        const nameByUser: Record<string, string> = Object.fromEntries(
          (users ?? []).map(u => [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email]))

        const built = raw.map(r => {
          const sid = studentByProject[r.project_id]
          const uid = sid ? userByStudent[sid] : undefined
          return { project_id: r.project_id, step: r.step, submitted_at: r.submitted_at, student_name: (uid && nameByUser[uid]) || 'Student' }
        }).sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime())

        setItems(built)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load pending submissions — please try again.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AssignmentTurnedInIcon color="warning" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Pending Submissions</Typography>
          {items.length > 0 && <Chip size="small" color="warning" label={items.length} />}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No submissions waiting for review.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map(it => (
              <Box
                key={`${it.project_id}-${it.step}`}
                onClick={() => navigate(`/review/${it.project_id}`)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                  border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.75, py: 1.1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Chip size="small" label={`Step ${it.step}: ${STEP_LABEL[it.step]}`} sx={{ height: 22, fontSize: '0.7rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>{it.student_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(it.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
